import "@/App.css";
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from '@/components/ui/sonner';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import ServicesSection from '@/components/ServicesSection';
import WhySection from '@/components/WhySection';
import WorkflowSection from '@/components/WorkflowSection';
import BeforeAfterSection from '@/components/BeforeAfterSection';
import FAQSection from '@/components/FAQSection';
import AuditForm from '@/components/AuditForm';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import GeoSeoSection from '@/components/GeoSeoSection';
import { lazy, Suspense, useEffect } from 'react';
import LegalMentions from '@/pages/LegalMentions';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsConditions from '@/pages/TermsConditions';
import NotFound from '@/pages/NotFound';

const RestaurantService = lazy(() => import('@/pages/RestaurantService'));
const MedicalService = lazy(() => import('@/pages/MedicalService'));
const RetailService = lazy(() => import('@/pages/RetailService'));
const GeoLandingFrance = lazy(() => import('@/pages/GeoLandingFrance'));
const GeoLandingTunisie = lazy(() => import('@/pages/GeoLandingTunisie'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse">Chargement...</div>
  </div>
);

function HomePage() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [hash]);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <div className="section-divider" />
        <ServicesSection />
        <WhySection />
        <WorkflowSection />
        <BeforeAfterSection />
        <FAQSection />
        <GeoSeoSection />
        <div className="section-divider" />
        <AuditForm />
        <div className="section-divider" />
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <div className="min-h-screen bg-background text-foreground">
          <SEOHead />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/services/restaurant"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RestaurantService />
                </Suspense>
              }
            />
            <Route
              path="/services/medical"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <MedicalService />
                </Suspense>
              }
            />
            <Route
              path="/services/retail"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RetailService />
                </Suspense>
              }
            />
            <Route
              path="/agence-automatisation-ia-france"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <GeoLandingFrance />
                </Suspense>
              }
            />
            <Route
              path="/agence-automatisation-ia-tunisie"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <GeoLandingTunisie />
                </Suspense>
              }
            />
            <Route path="/mentions-legales" element={<LegalMentions />} />
            <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
            <Route path="/cgv" element={<TermsConditions />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster position="bottom-right" />
        </div>
      </ThemeProvider>
    </Router>
  );
}

export default App;
